import React, { useState, useMemo, useRef } from 'react';
import html2pdf from 'html2pdf.js'; 
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { useNavigate } from 'react-router-dom'
import './App.css'; 
import logo from './logo.png';
import { logout } from "./utils/logout";

const COLORS = ['#02AD24', '#CD0029'];
const gaugeTrackColor = '#e6e6e6';

const CustomDonutTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #ccc',
      borderRadius: '8px',
      padding: '10px 14px',
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
      fontSize: '14px'
    }}>
      {payload.map((entry, index) => (
        <div key={index}>
          <strong>{entry.name} Cost:</strong> ${entry.value.toLocaleString()}
        </div>
      ))}
    </div>
  );
};

const CustomBarTooltip = ({ active, label, data, viewMode }) => {
  if (!active || !label || !data || !viewMode) return null;

  const row = data.find(d => (d["Friendly Name"] || d["License Type"]) === label);
  if (!row) return null;

  const assigned = row["Assigned Licenses"];
  const unassigned = row["Unassigned Licenses"];

  let assignedCost = 0;
  let unassignedCost = 0;

  if (viewMode === "Monthly") {
    assignedCost = row["Assigned Cost (Monthly)"];
    unassignedCost = row["Unassigned Cost (Monthly)"];
  } else if (viewMode === "Quarterly") {
    assignedCost = (row["Assigned Cost (Monthly)"] || 0) * 3;
    unassignedCost = (row["Unassigned Cost (Monthly)"] || 0) * 3;
  } else {
    assignedCost = row["Assigned Cost (Annual)"];
    unassignedCost = row["Unassigned Cost (Annual)"];
  }

  const percentAssigned = assigned / (assigned + unassigned || 1);

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #ccc',
      borderRadius: '8px',
      padding: '10px 14px',
      boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
      fontSize: '14px',
      color: '#000'
    }}>
      <div style={{ fontWeight: 600 }}>{label}</div>
      <div>Assigned: {assigned.toLocaleString()}</div>
      <div>Assigned cost: ${assignedCost.toLocaleString()}</div>
      <div>Unassigned: {unassigned.toLocaleString()}</div>
      <div>Unassigned cost: ${unassignedCost.toLocaleString()}</div>
      <div>Assigned: {Math.round(percentAssigned * 100)}%</div>
    </div>
  );
};


const ChartsDashboard = ({ licenseData }) => {
  const exportRef = useRef();
  const [licenseTypeFilter, setLicenseTypeFilter] = useState("All");
  const [viewMode, setViewMode] = useState("Annual");
  const [isExporting, setIsExporting] = useState(false);
  const navigate = useNavigate();

  const handleExportToPDF = () => {
    setIsExporting(true);

    // Delay to ensure React re-renders the logo
    setTimeout(() => {
      const element = exportRef.current;
      const clone = element.cloneNode(true);
  
      // Inject logo at top of clone without rendering in UI
      const logoDiv = document.createElement('div');
      logoDiv.style.textAlign = 'center';
      logoDiv.style.paddingBottom = '20px';

      const logoImg = document.createElement('img');
      logoImg.src = '/logo.png';
      logoImg.alt = 'Logo';
      logoImg.style.height = '80px';

      logoDiv.appendChild(logoImg);
      clone.prepend(logoDiv); // Insert logo at the top

      const exportButtons = clone.querySelectorAll("button");
      exportButtons.forEach(btn => {
        if (btn.innerText.trim() === "Export to PDF") {
          btn.style.display = "none";
        }
      });

      clone.style.all = 'initial';
      clone.classList.add('pdf-export-mode');
      clone.style.margin = '0 auto';
      clone.style.width = '794px'; // A4 width in px at 96dpi
      clone.style.maxWidth = '794px'; 
      clone.style.boxSizing = 'border-box';
      clone.style.fontFamily = 'Inter, sans-serif';

      const opt = {
        margin: [0,20],
        filename: 'PowerAudit - Licensing Costs.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2.5,
          useCORS: true
        },
        jsPDF: {
          unit: 'pt',
          format: 'a4',
          orientation: 'portrait'
        }
      };

      requestAnimationFrame(() => {
        html2pdf()
          .from(clone)
          .set(opt)
          .output('blob')
          .then(blob => {
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = 'PowerAudit - Licensing Costs.pdf';
            link.click();
            URL.revokeObjectURL(link.href);
            setIsExporting(false);
          });
      });
    }, 50);
  };
   
  
  const filteredData = useMemo(() => {
    const pricedData = licenseData.filter(row =>
      (row["Assigned Cost (Monthly)"] || 0) > 0 ||
      (row["Unassigned Cost (Monthly)"] || 0) > 0 ||
      (row["Assigned Cost (Annual)"] || 0) > 0 ||
      (row["Unassigned Cost (Annual)"] || 0) > 0
    );
  
    return licenseTypeFilter === "All"
      ? pricedData
      : pricedData.filter(d => d["License Type"] === licenseTypeFilter);
  }, [licenseData, licenseTypeFilter]);
  

  const totalAssignedLicenses = filteredData.reduce((sum, row) => sum + row["Assigned Licenses"], 0);
  const totalUnassignedLicenses = filteredData.reduce((sum, row) => sum + row["Unassigned Licenses"], 0);
  const percentAssignedLicenses = totalAssignedLicenses / (totalAssignedLicenses + totalUnassignedLicenses || 1);


  const totalAssigned = filteredData.reduce((sum, row) => {
    const value = viewMode === "Quarterly"
      ? row["Assigned Cost (Monthly)"] * 3
      : row[viewMode === "Monthly" ? "Assigned Cost (Monthly)" : "Assigned Cost (Annual)"];
    return sum + (value || 0);
  }, 0);

  const totalUnassigned = filteredData.reduce((sum, row) => {
    const value = viewMode === "Quarterly"
      ? row["Unassigned Cost (Monthly)"] * 3
      : row[viewMode === "Monthly" ? "Unassigned Cost (Monthly)" : "Unassigned Cost (Annual)"];
    return sum + (value || 0);
  }, 0);

  const percentAssigned = totalAssigned / (totalAssigned + totalUnassigned || 1);

  const donutData = [
    { name: "Assigned", value: totalAssigned },
    { name: "Unassigned", value: totalUnassigned }
  ];

  const barData = licenseData
  .map(row => {
    const assignedLicenses = parseInt(row["Assigned Licenses"] || 0, 10);
    const unassignedLicenses = parseInt(row["Unassigned Licenses"] || 0, 10);

    const assignedCost = parseFloat(
      viewMode === "Quarterly"
        ? (row["Assigned Cost (Monthly)"] || 0) * 3
        : viewMode === "Monthly"
          ? row["Assigned Cost (Monthly)"] || 0
          : row["Assigned Cost (Annual)"] || 0
    );

    const unassignedCost = parseFloat(
      viewMode === "Quarterly"
        ? (row["Unassigned Cost (Monthly)"] || 0) * 3
        : viewMode === "Monthly"
          ? row["Unassigned Cost (Monthly)"] || 0
          : row["Unassigned Cost (Annual)"] || 0
    );

    return {
      name: row["Friendly Name"] || row["License Type"],
      Assigned: assignedLicenses,
      Unassigned: unassignedLicenses,
      assignedCost,
      unassignedCost
    };
  })
  .filter(row => (row.assignedCost + row.unassignedCost) > 0);


const dynamicBarHeight = Math.max(300, barData.length * 40);


  return (
    <div style={{
      padding: '20px',
      maxWidth: '1000px',
      margin: '0 auto',
      fontFamily: 'Inter, sans-serif',
    }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px' }}>
        <img src={logo} alt="Logo" style={{ height: '80px' }} />
                <button
                  className="no-print"
                  onClick={() => logout(navigate)}
                  style={{
                    marginLeft: 'auto',
                    backgroundColor: '#CD0029',
                    color: 'white',
                    border: 'none',
                    padding: '10px 16px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  Logout
                </button>
      </div>

      <div
        ref={exportRef}
        style={{
          background: '#fff',
          borderRadius: '16px',
          padding: '40px 32px',
          boxShadow: '0 12px 28px rgba(0, 0, 0, 0.06)',
          width: '100%',
          maxWidth: '1400px',
          overflow: 'hidden'
        }}
      >

        {viewMode === "Annual" && (
          <div className="no-print" style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }} >
            <button
              onClick={() => handleExportToPDF()}
              style={{
                padding: '10px 20px',
                fontSize: '16px',
                backgroundColor: '#02AD24',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
              }}
            >
              Export to PDF
            </button>
          </div>
        )}


        {/* Filters */}
        <div style={{
          display: 'flex',
          gap: '24px',
          marginBottom: '32px',
          flexWrap: 'wrap',
          alignItems: 'flex-start'
        }}>
          <div>
            <label style={{ fontWeight: 500, fontSize: '14px', display: 'block', marginBottom: '6px' }}>License Type</label>
            <select
              value={licenseTypeFilter}
              onChange={e => setLicenseTypeFilter(e.target.value)}
              style={{
                padding: '10px 14px',
                borderRadius: '8px',
                border: '1px solid #ccc',
                fontSize: '15px'
              }}
            >
              <option>All</option>
                {[...new Set(
                  licenseData
                    .filter(row =>
                      (row["Assigned Cost (Monthly)"] || 0) > 0 ||
                      (row["Unassigned Cost (Monthly)"] || 0) > 0 ||
                      (row["Assigned Cost (Annual)"] || 0) > 0 ||
                      (row["Unassigned Cost (Annual)"] || 0) > 0
                    )
                    .map(d => d["Friendly Name"] || d["License Type"])
                )].map((type, i) => (
                  <option key={i}>{type}</option>
                ))}
            </select>
          </div>

          <div>
            <label style={{ fontWeight: 500, fontSize: '14px', display: 'block', marginBottom: '6px' }}> &nbsp;</label>
            <select
              value={viewMode}
              onChange={e => setViewMode(e.target.value)}
              style={{
                padding: '10px 14px',
                borderRadius: '8px',
                border: '1px solid #ccc',
                fontSize: '15px'
              }}
            >
              <option>Annual</option>
              <option>Quarterly</option>
              <option>Monthly</option>
            </select>
          </div>
        </div>
        
        {/* Top charts + Bar chart aligned */}
        <div style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center'
        }}>
          <div style={{
            width: '100%',
            maxWidth: '1120px',
            boxSizing: 'border-box',
            padding: '0 20px'  // to give some breathing room on large screens
          }}>
            {/* Gauge + Donut */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              columnGap: '40px',
              marginBottom: '50px',
              flexWrap: 'nowrap',
              width: '100%',
              boxSizing: 'border-box'
            }}>
            {/* Vertical Bar Chart replacing Gauge */}
            <div style={{
              width: 'calc(50% - 20px)',
              boxSizing: 'border-box',
              background: '#f9f9f9',
              padding: '20px',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}>
              <h3 style={{ fontSize: '24px', fontWeight: 600, marginBottom: '16px' }}>
                Overall Licensing Health
              </h3>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart
                  data={[
                    { name: 'Assigned', value: totalAssignedLicenses },
                    { name: 'Unassigned', value: totalUnassignedLicenses }
                  ]}
                  margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
                >
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip formatter={(val) => val.toLocaleString()} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    <Cell fill="#02AD24" />
                    <Cell fill="#CD0029" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>


              {/* Donut */}
              <div style={{
                width: 'calc(50% - 20px)',
                boxSizing: 'border-box',
                background: '#f9f9f9',
                padding: '20px',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}>
                <h3 style={{ fontSize: '24px', fontWeight: 600, textAlign: 'center', marginBottom: '16px' }}>
                  Microsoft Licensing – {viewMode} Cost
                </h3>
                <div style={{ width: '100%', minWidth: 0 }}>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={donutData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        dataKey="value"
                        label={({ name, percent, cx, cy, midAngle, outerRadius }) => {
                          const RADIAN = Math.PI / 180;
                          const radiusOffset = 60;
                          const radius = outerRadius + radiusOffset;
                          const x = cx + radius * Math.cos(-midAngle * RADIAN);
                          const y = cy + radius * Math.sin(-midAngle * RADIAN);

                          return (
                            <text
                              x={x}
                              y={y}
                              fill="black"
                              textAnchor="middle"
                              dominantBaseline="central"
                              fontSize={14}
                              style={{
                                maxWidth: 80,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'inline-block'
                              }}
                            >
                              {`${name}: ${Math.round(percent * 100)}%`}
                            </text>
                          );
                        }}
                      >
                        {donutData.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomDonutTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Bar Chart */}
            <div style={{
              width: '100%',
              background: '#f9f9f9',
              padding: '20px',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              boxSizing: 'border-box'
            }}>
              <h3 style={{
                fontSize: '24px',
                fontWeight: 600,
                textAlign: 'center',
                marginBottom: '20px',
                color: '#333'
              }}>
                Microsoft Licensing by Type – {viewMode} Cost
              </h3>
              <div style={{ width: '100%', minWidth: 0 }}>
                <ResponsiveContainer width="100%" height={dynamicBarHeight}>
                  <BarChart
                    data={barData}
                    layout="vertical"
                    margin={{ left: 80, right: 40 }}
                    barCategoryGap={20}
                    isAnimationActive={false}
                  >
                    <XAxis type="number" tickFormatter={val => val.toLocaleString()} />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={140}
                    />
                    <Tooltip cursor={false} content={<CustomBarTooltip data={filteredData} viewMode={viewMode} />} />
                    <Legend align="left" verticalAlign="bottom" formatter={(value) => <span style={{ color: '#000' }}>{value}</span>} />
                    <Bar dataKey="Assigned" stackId="a" fill="#02AD24" barSize={60} activeBar={false} />
                    <Bar dataKey="Unassigned" stackId="a" fill="#CD0029" barSize={60} activeBar={false} />
                  </BarChart>
                </ResponsiveContainer>
              </div>  
            </div>

          </div>
        </div>

        {/* Cost Savings Button */}
        <div className="no-print" style={{ textAlign: 'center', marginTop: '50px' }}>
          <button
            onClick={() => navigate('/savings')}
            style={{
              padding: '14px 28px',
              fontSize: '16px',
              backgroundColor: '#02AD24',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
            }}
          >
            See Cost Savings Analysis
          </button>
        </div>

      </div>
    </div>
  );
};

export default ChartsDashboard;
